import { useEffect, useRef } from 'react';
import {
  InterviewEntity,
  useFindManyInterviewsQuery,
} from 'src/store/interviewApi';
import { useDispatch } from 'react-redux';
import { setInterviews } from 'src/store/interviewSlice';
import Outline from './outline';
import Content, { ContentRef } from './content';
import Editor, { EditorRef } from './editor';
import Chat, { ChatRef } from './chat';
import {
  subscribeEvent,
  SocketEvent,
  unsubscribeEvent,
} from 'src/store/socket';
import { UserConversationEntity, MessageEntity } from 'src/store/chatApi';
import { setMessage } from 'src/store/messagesSlice';

interface OnSocketNewMessage {
  userConversation: UserConversationEntity;
  message: MessageEntity;
}

const Interviews = () => {
  const { data: interviewData, refetch } = useFindManyInterviewsQuery();
  const dispatch = useDispatch();
  const chatRef = useRef<ChatRef>(null);
  const editorRef = useRef<EditorRef>(null);
  const contentRef = useRef<ContentRef>(null);

  const onTestInterview = (interviewId: number) => {
    chatRef.current?.onTestInterview(interviewId);
  };

  useEffect(() => {
    if (!interviewData?.data) return;
    dispatch(setInterviews(interviewData.data));
  }, [interviewData, dispatch]);

  const onAddNew = () => {
    editorRef.current?.onCreate();
  };

  const onSelectInterview = (interview: InterviewEntity, scroll = false) => {
    editorRef.current?.onSelect(interview);
    if (scroll) {
      contentRef.current?.scrollTo(interview.id);
    }
  };

  useEffect(() => {
    const onNewMessage = ({ message }: OnSocketNewMessage) => {
      dispatch(setMessage(message));
    };
    subscribeEvent(SocketEvent.NEW_MESSAGE, onNewMessage);
    return () => {
      unsubscribeEvent(SocketEvent.NEW_MESSAGE, onNewMessage);
    };
  }, [dispatch]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '260px 1fr 440px 380px',
        height: 'calc(100vh - 56px)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          borderRight: '1px solid var(--border)',
        }}
      >
        <Outline onAddNew={onAddNew} setSelectedInterview={onSelectInterview} />
      </div>
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          borderRight: '1px solid var(--border)',
        }}
      >
        <Content ref={contentRef} setSelectedInterview={onSelectInterview} />
      </div>
      <div
        style={{
          overflow: 'hidden',
          display: 'flex',
          borderRight: '1px solid var(--border)',
        }}
      >
        <Editor
          ref={editorRef}
          refetchAll={refetch}
          onTestInterview={onTestInterview}
        />
      </div>
      <div style={{ overflow: 'hidden', display: 'flex' }}>
        <Chat ref={chatRef} />
      </div>
    </div>
  );
};

export default Interviews;
